import React, { useEffect } from "react";
import { Column, Query } from "material-table";
import { useSnackbar } from "notistack";
import { getReports, AccountingReport, companyInvoicePDF } from "api/owner/reports";
import moment from "moment";
import { DefaultTable } from "components";
import { useMounted } from "utils/hooks";
import i18n from "./i18n";
import "./documentsStyle.scss";

// prop types
interface ReportsTabProps {
    clubId?: string,
}

// currency formatter
const formatter = Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// owner accounting table for reports
const ReportsTab = ({ clubId }: ReportsTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<AccountingReport>) => {
        // paging
        const params: Parameters<typeof getReports>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // sorting
        if (query.orderBy) {
            params.sortOrder = query.orderDirection.toUpperCase() as typeof params["sortOrder"];
            params.sortKey = query.orderBy.field as typeof params["sortKey"];
        }

        // club filter
        params.clubId = clubId;

        return params;
    };

    // table ref for refresh
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [clubId]);

    // table actions
    const tableActions = [
        ({ resourceUrl }: AccountingReport) => ({
            hidden: !resourceUrl,
            icon: "get_app",
            tooltip: i18n.reports,
            onClick: () => {
                window.open(resourceUrl);
            },
        }),
        ({ companyInvoice, id }: AccountingReport) => ({
            hidden: !companyInvoice,
            icon: "article",
            tooltip: i18n.invoices,
            onClick: () => {
                companyInvoicePDF({ clubId, reportId: id }).then((response) => {
                    window.open(response!.invoicePdfUrl);
                });
            },
        }),
    ];

    // table columns
    const tableColumns: Column<AccountingReport>[] = [
        {
            field: "reportType",
            lookup: { "accounting-report": i18n.accountingReport, "playsports-invoice": i18n.playsportsInvoice, "expense-report": i18n.expenseReport },
            title: i18n.reportType,
        },
        {
            field: "recipientClub.shortName" || "expenseCompanyClub.shortName",
            sorting: false,
            title: i18n.club,
        },
        {
            field: "intervalStartDate",
            render: ({ intervalStartDate, intervalEndDate, reportingInterval }) => (reportingInterval === "custom" ? `${moment.utc(intervalStartDate).format("MMMM YYYY")} - ${moment.utc(intervalEndDate).format("MMMM YYYY")}` : moment.utc(intervalStartDate).format("MMMM YYYY")),
            title: i18n.timePeriod,
        },
        {
            field: "totalRevenueCents",
            render: ({ totalRevenueCents, reportType }) => (reportType !== "expense-report" ? (formatter.format(totalRevenueCents / 100)) : (formatter.format(0))),
            sorting: false,
            title: i18n.revenue,
        },
        {
            field: "totalRevenueCents",
            render: ({ totalRevenueCents, companyInvoice, reportType }) => (reportType === "expense-report" && companyInvoice ? (formatter.format(companyInvoice.totalAmountCents / 100)) : (formatter.format(totalRevenueCents / 100))),
            sorting: false,
            title: i18n.expense,
        },
    ];

    // render
    return (
        <DefaultTable
            actions={tableActions}
            columns={tableColumns}
            data={(query) => new Promise((resolve) => {
                const params = convertQueryToParams(query);
                getReports(params)
                    .then((response) => {
                        resolve({
                            ...response,
                            page: query.page,
                        });
                    })
                    .catch((err) => {
                        displayError(err);
                        resolve({ data: [], page: 0, totalCount: 0 });
                    });
            })}
            options={{ search: false }}
            tableRef={tableRef}
            title={i18n.reports}
        />
    );
};

export default ReportsTab;
